import { useState, useMemo, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Send, Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import {
  PublishQueueEntryCard,
  PublishQueueEntryCardSkeleton,
} from '@/components/publish/PublishQueueEntryCard';
import { PublishConfirmDialog } from '@/components/publish/PublishConfirmDialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import type { ConfirmationPayload, PublishQueueEntry } from '@/types/api';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'approved', label: 'Approved' },
  { value: 'queued', label: 'Queued' },
  { value: 'failed', label: 'Failed' },
] as const;

export function PublishQueuePage() {
  const user = useAuthStore((s) => s.user);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const queryClient = useQueryClient();

  const [selectedEntryIds, setSelectedEntryIds] = useState<Set<string>>(new Set());
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [confirmationPayload, setConfirmationPayload] = useState<ConfirmationPayload | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const apiFilters: Record<string, string | number> = {};
  if (selectedStatus !== 'all') apiFilters.status = selectedStatus;
  if (selectedDepartment !== 'all') apiFilters.department = selectedDepartment;

  const {
    data: response,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['publish-queue', apiFilters],
    queryFn: () => api.publishQueue.list(apiFilters),
    enabled: !!user,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const entries = (response as unknown as { entries: PublishQueueEntry[]; total: number })?.entries ?? [];

  const departments = useMemo(() => {
    const depts = new Set(entries.map((e: PublishQueueEntry) => e.department));
    return Array.from(depts).sort();
  }, [entries]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedEntryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const buildMutation = useMutation({
    mutationFn: () =>
      api.publishQueue.buildConfirmation(Array.from(selectedEntryIds)),
    onSuccess: (payload) => {
      setConfirmationPayload(payload);
      setShowConfirmDialog(true);
    },
    onError: () => {
      toast.error('Failed to build confirmation. Please try again.');
    },
  });

  const publishMutation = useMutation({
    mutationFn: () =>
      api.publishQueue.confirm(
        confirmationPayload!.confirmationId,
        Array.from(selectedEntryIds),
      ),
    onSuccess: () => {
      toast.success(
        `${selectedEntryIds.size} document${selectedEntryIds.size !== 1 ? 's' : ''} queued for publishing.`,
      );
      setSelectedEntryIds(new Set());
      setShowConfirmDialog(false);
      setConfirmationPayload(null);
      queryClient.invalidateQueries({ queryKey: ['publish-queue'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
    },
    onError: () => {
      toast.error('Publish failed. Please try again.');
    },
  });

  if (!user || isLoading) {
    return (
      <div className="space-y-4 p-6">
        {Array.from({ length: 4 }, (_, i) => (
          <PublishQueueEntryCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (!hasPermission('bulk_publish')) {
    return <Navigate to="/" replace />;
  }

  const allSelected = entries.length > 0 && entries.every((e: PublishQueueEntry) => selectedEntryIds.has(e.entryId));
  const someSelected = entries.some((e: PublishQueueEntry) => selectedEntryIds.has(e.entryId));

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedEntryIds(new Set());
    } else {
      setSelectedEntryIds(new Set(entries.map((e: PublishQueueEntry) => e.entryId)));
    }
  };

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-6">
      <PageHeader
        title="Publish Queue"
        subtitle="Approved documents ready to publish"
        actions={
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              {selectedEntryIds.size} selected
            </span>
            <Button
              className="bg-teal text-white hover:bg-teal/90"
              size="sm"
              disabled={selectedEntryIds.size === 0 || buildMutation.isPending}
              onClick={() => buildMutation.mutate()}
            >
              {buildMutation.isPending && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              <Send className="mr-1.5 h-3.5 w-3.5" />
              Publish Selected
            </Button>
          </div>
        }
      />

      {/* Filter Bar */}
      <div className="flex items-center gap-3">
        <Select value={selectedDepartment} onValueChange={(v) => setSelectedDepartment(v ?? 'all')}>
          <SelectTrigger size="sm">
            <SelectValue placeholder="All Departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {departments.map((dept) => (
              <SelectItem key={dept} value={dept}>
                {dept}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedStatus} onValueChange={(v) => setSelectedStatus(v ?? 'all')}>
          <SelectTrigger size="sm">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Select All Bar */}
      {entries.length > 0 && (
        <div className="flex items-center gap-3 rounded-md border border-border bg-muted/30 px-4 py-2.5">
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onCheckedChange={handleSelectAll}
          />
          <span className="text-sm text-muted-foreground">
            Select all {entries.length} document{entries.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Entry List */}
      <div className="space-y-3">
        {isLoading ? (
          Array.from({ length: 4 }, (_, i) => (
            <PublishQueueEntryCardSkeleton key={i} />
          ))
        ) : isError ? (
          <EmptyState
            icon={Send}
            title="Failed to load queue"
            description="Something went wrong. Please try again."
            action={
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : entries.length === 0 ? (
          <EmptyState
            icon={Send}
            title="Nothing to publish"
            description="Approved documents will appear here when ready."
          />
        ) : (
          entries.map((entry: PublishQueueEntry) => (
            <PublishQueueEntryCard
              key={entry.entryId}
              entry={entry}
              isSelected={selectedEntryIds.has(entry.entryId)}
              onToggleSelect={toggleSelect}
            />
          ))
        )}
      </div>

      {/* Confirm Dialog */}
      <PublishConfirmDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        payload={confirmationPayload}
        onConfirm={() => publishMutation.mutate()}
        isLoading={publishMutation.isPending}
      />
    </div>
  );
}
